/**
 * Canonical repair operations.
 *
 * Invoice line items arrive as free text ("HOOD ASSY R&R", "replace hood, incl paint"),
 * so they must collapse onto a stable key before per-operation costs can be averaged.
 * These keys are the join between an invoice line item, a reported damage on the
 * insurance ACV assessment, and an AI photo damage report.
 *
 * Keys are persisted downstream — rename labels freely, never rename keys.
 */
export const REPAIR_OPERATIONS = [
    // --- Hood ---
    {
        key: 'hood_replacement',
        label: 'Hood Replacement',
        categoryKey: 'body_interior',
        components: ['hood'],
        action: 'replace',
        aliases: ['hood assy', 'hood assembly', 'hood panel'],
    },
    {
        key: 'hood_repair',
        label: 'Hood Repair',
        categoryKey: 'body_interior',
        components: ['hood'],
        action: 'repair',
    },
    {
        key: 'hood_hinge_replacement',
        label: 'Hood Hinge Replacement',
        categoryKey: 'body_interior',
        components: ['hood hinge'],
        action: 'replace',
        aliases: ['hinge, hood', 'hood hinges'],
    },
    {
        key: 'hood_latch_replacement',
        label: 'Hood Latch Replacement',
        categoryKey: 'body_interior',
        components: ['hood latch', 'hood catch'],
        action: 'replace',
        aliases: ['hood latches'],
    },
    // --- Body & exterior ---
    {
        key: 'front_bumper_replacement',
        label: 'Front Bumper Replacement',
        categoryKey: 'body_interior',
        components: ['front bumper'],
        action: 'replace',
        aliases: ['bumper, front', 'front bmpr', 'front bumper cover'],
    },
    {
        key: 'rear_bumper_replacement',
        label: 'Rear Bumper Replacement',
        categoryKey: 'body_interior',
        components: ['rear bumper'],
        action: 'replace',
        aliases: ['bumper, rear'],
    },
    {
        key: 'bumper_repair',
        label: 'Bumper Repair',
        categoryKey: 'body_interior',
        components: ['bumper'],
        action: 'repair',
    },
    {
        key: 'fender_replacement',
        label: 'Fender Replacement',
        categoryKey: 'body_interior',
        components: ['fender'],
        action: 'replace',
    },
    {
        key: 'mirror_replacement',
        label: 'Mirror Replacement',
        categoryKey: 'body_interior',
        components: ['mirror', 'side mirror', 'west coast mirror'],
        action: 'replace',
    },
    {
        key: 'grille_replacement',
        label: 'Grille Replacement',
        categoryKey: 'body_interior',
        components: ['grille', 'grill'],
        action: 'replace',
    },
    {
        key: 'body_panel_repair',
        label: 'Body Panel Repair',
        categoryKey: 'body_interior',
        components: ['body panel', 'side panel', 'quarter panel', 'roof panel', 'corner cap'],
        action: 'any',
    },
    {
        key: 'paint_refinish',
        label: 'Paint / Refinish',
        categoryKey: 'body_interior',
        components: ['paint', 'refinish', 'repaint', 'clear coat'],
        action: 'service',
    },
    {
        key: 'rust_repair',
        label: 'Rust Repair',
        categoryKey: 'body_interior',
        components: ['rust', 'corrosion'],
        action: 'repair',
    },
    // --- Glass ---
    {
        key: 'windshield_replacement',
        label: 'Windshield Replacement',
        categoryKey: 'body_interior',
        components: ['windshield', 'front glass', 'windscreen'],
        action: 'replace',
    },
    {
        key: 'side_glass_replacement',
        label: 'Side Glass Replacement',
        categoryKey: 'body_interior',
        components: ['side glass', 'door glass', 'side window', 'vent glass'],
        action: 'replace',
    },
    {
        key: 'rear_glass_replacement',
        label: 'Rear Glass Replacement',
        categoryKey: 'body_interior',
        components: ['rear glass', 'back glass', 'rear window'],
        action: 'replace',
    },
    // --- Cooling ---
    {
        key: 'radiator_replacement',
        label: 'Radiator Replacement',
        categoryKey: 'powertrain',
        components: ['radiator'],
        action: 'replace',
        aliases: ['radiator assy', 'radiator assembly'],
    },
    {
        key: 'radiator_support_replacement',
        label: 'Radiator Support Replacement',
        categoryKey: 'body_interior',
        components: ['radiator support', 'rad support', 'core support'],
        action: 'replace',
    },
    {
        key: 'coolant_reservoir_replacement',
        label: 'Coolant Reservoir Replacement',
        categoryKey: 'powertrain',
        components: [
            'coolant reservoir',
            'coolant tank',
            'overflow tank',
            'surge tank',
            'expansion tank',
            'recovery tank',
        ],
        action: 'replace',
    },
    {
        key: 'water_pump_replacement',
        label: 'Water Pump Replacement',
        categoryKey: 'powertrain',
        components: ['water pump'],
        action: 'replace',
    },
    {
        key: 'thermostat_replacement',
        label: 'Thermostat Replacement',
        categoryKey: 'powertrain',
        components: ['thermostat'],
        action: 'replace',
    },
    {
        key: 'coolant_hose_replacement',
        label: 'Coolant Hose Replacement',
        categoryKey: 'powertrain',
        components: ['radiator hose', 'coolant hose', 'heater hose'],
        action: 'replace',
    },
    // --- Engine ---
    {
        key: 'engine_replacement',
        label: 'Engine Replacement',
        categoryKey: 'powertrain',
        components: ['engine', 'motor'],
        action: 'replace',
        aliases: [
            'engine replacement',
            'replacement engine',
            'reman engine',
            'remanufactured engine',
            'engine swap',
            'long block',
            'short block',
            'crate engine',
        ],
    },
    {
        key: 'engine_repair',
        label: 'Engine Repair',
        categoryKey: 'powertrain',
        components: ['engine', 'motor'],
        action: 'repair',
        aliases: ['engine overhaul', 'engine rebuild'],
    },
    {
        key: 'head_gasket_replacement',
        label: 'Head Gasket Replacement',
        categoryKey: 'powertrain',
        components: ['head gasket', 'cylinder head'],
        action: 'replace',
    },
    {
        key: 'turbocharger_replacement',
        label: 'Turbocharger Replacement',
        categoryKey: 'powertrain',
        components: ['turbocharger', 'turbo'],
        action: 'replace',
    },
    {
        key: 'fuel_pump_replacement',
        label: 'Fuel Pump Replacement',
        categoryKey: 'powertrain',
        components: ['fuel pump'],
        action: 'replace',
    },
    {
        key: 'fuel_injector_replacement',
        label: 'Fuel Injector Replacement',
        categoryKey: 'powertrain',
        components: ['fuel injector', 'injector'],
        action: 'replace',
    },
    {
        key: 'exhaust_repair',
        label: 'Exhaust Repair',
        categoryKey: 'powertrain',
        components: ['exhaust', 'muffler', 'tailpipe', 'catalytic converter'],
        action: 'any',
    },
    {
        key: 'oil_change',
        label: 'Oil Change',
        categoryKey: 'powertrain',
        components: ['oil change', 'lube oil filter'],
        action: 'service',
        aliases: ['lof service'],
    },
    // --- Transmission & drivetrain ---
    {
        key: 'transmission_replacement',
        label: 'Transmission Replacement',
        categoryKey: 'powertrain',
        components: ['transmission'],
        action: 'replace',
        aliases: ['reman transmission', 'remanufactured transmission', 'transmission swap'],
    },
    {
        key: 'transmission_repair',
        label: 'Transmission Repair',
        categoryKey: 'powertrain',
        components: ['transmission'],
        action: 'repair',
        aliases: ['transmission rebuild', 'transmission overhaul'],
    },
    {
        key: 'clutch_replacement',
        label: 'Clutch Replacement',
        categoryKey: 'powertrain',
        components: ['clutch'],
        action: 'replace',
    },
    {
        key: 'driveshaft_repair',
        label: 'Driveshaft / U-Joint',
        categoryKey: 'powertrain',
        components: ['driveshaft', 'drive shaft', 'u-joint', 'universal joint', 'carrier bearing'],
        action: 'any',
    },
    {
        key: 'differential_repair',
        label: 'Differential Repair',
        categoryKey: 'powertrain',
        components: ['differential', 'rear end'],
        action: 'any',
    },
    // --- Brakes ---
    {
        key: 'brake_pad_replacement',
        label: 'Brake Pad Replacement',
        categoryKey: 'brakes',
        components: ['brake pad', 'brake shoe'],
        action: 'replace',
        aliases: ['pads and rotors', 'brake pads'],
    },
    {
        key: 'brake_rotor_replacement',
        label: 'Brake Rotor Replacement',
        categoryKey: 'brakes',
        components: ['brake rotor', 'rotor', 'brake drum'],
        action: 'replace',
    },
    {
        key: 'brake_caliper_replacement',
        label: 'Brake Caliper Replacement',
        categoryKey: 'brakes',
        components: ['brake caliper', 'caliper'],
        action: 'replace',
    },
    {
        key: 'brake_service',
        label: 'Brake Service',
        categoryKey: 'brakes',
        components: ['brake', 'brake line', 'master cylinder'],
        action: 'any',
    },
    // --- Tires & wheels ---
    {
        key: 'tire_replacement',
        label: 'Tire Replacement',
        categoryKey: 'tires_wheels',
        components: ['tire', 'tyre'],
        action: 'replace',
        aliases: ['tires', 'mount and balance'],
    },
    {
        key: 'wheel_alignment',
        label: 'Wheel Alignment',
        categoryKey: 'tires_wheels',
        components: ['alignment'],
        action: 'service',
        aliases: ['front end alignment'],
    },
    {
        key: 'wheel_replacement',
        label: 'Wheel / Rim Replacement',
        categoryKey: 'tires_wheels',
        components: ['wheel', 'rim'],
        action: 'replace',
    },
    {
        key: 'wheel_bearing_replacement',
        label: 'Wheel Bearing Replacement',
        categoryKey: 'tires_wheels',
        components: ['wheel bearing', 'hub bearing', 'hub assembly'],
        action: 'replace',
    },
    // --- Electrical ---
    {
        key: 'battery_replacement',
        label: 'Battery Replacement',
        categoryKey: 'electrical',
        components: ['battery'],
        action: 'replace',
    },
    {
        key: 'alternator_replacement',
        label: 'Alternator Replacement',
        categoryKey: 'electrical',
        components: ['alternator'],
        action: 'replace',
    },
    {
        key: 'starter_replacement',
        label: 'Starter Replacement',
        categoryKey: 'electrical',
        components: ['starter'],
        action: 'replace',
    },
    {
        key: 'wiring_repair',
        label: 'Wiring Repair',
        categoryKey: 'electrical',
        components: ['wiring', 'wire harness', 'harness'],
        action: 'any',
    },
    {
        key: 'lighting_replacement',
        label: 'Lighting Replacement',
        categoryKey: 'electrical',
        components: ['headlight', 'tail light', 'taillight', 'marker light', 'turn signal'],
        action: 'replace',
    },
    {
        key: 'backup_camera_replacement',
        label: 'Backup Camera Replacement',
        categoryKey: 'electrical',
        components: ['backup camera', 'back up camera', 'reverse camera', 'camera'],
        action: 'replace',
    },
    // --- Heating & air ---
    {
        key: 'ac_compressor_replacement',
        label: 'A/C Compressor Replacement',
        categoryKey: 'heating_air',
        components: ['a/c compressor', 'ac compressor', 'air conditioning compressor'],
        action: 'replace',
    },
    {
        key: 'hvac_service',
        label: 'HVAC Service',
        categoryKey: 'heating_air',
        components: ['a/c', 'hvac', 'air conditioning', 'heater core', 'blower motor'],
        action: 'any',
    },
    // --- Suspension & steering ---
    {
        key: 'shock_strut_replacement',
        label: 'Shock / Strut Replacement',
        categoryKey: 'suspension_steering',
        components: ['shock absorber', 'shock', 'strut'],
        action: 'replace',
    },
    {
        key: 'leaf_spring_replacement',
        label: 'Leaf Spring Replacement',
        categoryKey: 'suspension_steering',
        components: ['leaf spring', 'spring pack'],
        action: 'replace',
    },
    {
        key: 'steering_repair',
        label: 'Steering Repair',
        categoryKey: 'suspension_steering',
        components: ['tie rod', 'ball joint', 'power steering', 'steering gear', 'steering'],
        action: 'any',
    },
    // --- Cargo body ---
    {
        key: 'rollup_door_repair',
        label: 'Roll-Up Door Repair',
        categoryKey: 'body_interior',
        components: ['roll-up door', 'rollup door', 'roll up door', 'cargo door', 'overhead door'],
        action: 'any',
    },
    {
        key: 'door_latch_replacement',
        label: 'Door Latch / Handle Replacement',
        categoryKey: 'body_interior',
        components: ['door latch', 'door handle'],
        action: 'replace',
    },
    {
        key: 'door_spring_replacement',
        label: 'Door Spring Replacement',
        categoryKey: 'body_interior',
        components: ['door spring', 'torsion spring'],
        action: 'replace',
    },
    {
        key: 'shelving_repair',
        label: 'Shelving Repair',
        categoryKey: 'body_interior',
        components: ['shelving', 'shelves', 'shelf'],
        action: 'any',
    },
];
const OPERATIONS_BY_KEY = new Map(REPAIR_OPERATIONS.map(op => [op.key, op]));
export const getRepairOperation = (key) => OPERATIONS_BY_KEY.get(key);
const ACTION_TOKENS = {
    replace: [
        'replacement',
        'replaced',
        'replace',
        'remanufactured',
        'reman',
        'r&r',
        'r & r',
        'r/r',
        'swap',
        'install new',
        'new',
    ],
    repair: [
        'repair',
        'rebuild',
        'overhaul',
        'straighten',
        'resurface',
        'realign',
        'patch',
        'weld',
        'fix',
    ],
    service: ['service', 'maintenance', 'inspect', 'flush', 'diagnose', 'adjust', 'clean'],
};
/**
 * Lowercase and flatten punctuation to spaces, preserving the characters that
 * carry meaning on invoices ("a/c", "r&r", "roll-up").
 */
export const normalizeRepairText = (text) => ` ${text.toLowerCase().replace(/[^a-z0-9&/\- ]+/g, ' ').replace(/\s+/g, ' ').trim()} `;
/** Strongest action verb present in the text, or null when the text does not say. */
export const detectRepairAction = (text) => {
    const normalized = normalizeRepairText(text);
    let best = null;
    let bestScore = 0;
    Object.entries(ACTION_TOKENS).forEach(([action, tokens]) => {
        tokens.forEach(token => {
            if (normalized.includes(token) && token.length > bestScore) {
                bestScore = token.length;
                best = action;
            }
        });
    });
    return best;
};
const ALIAS_BONUS = 4;
const ACTION_BONUS = 6;
const ACTION_MISMATCH_PENALTY = 4;
const MIN_SCORE = 3;
/**
 * Resolve free-text (an invoice line item or a reported damage) to a canonical
 * operation. Specificity wins by matched-substring length, so "radiator support"
 * beats "radiator" and "hood latch" beats "hood"; the action verb then separates
 * same-component operations like replacement vs repair.
 *
 * Returns null rather than guessing when nothing matches — callers must show
 * "no comparable data" instead of inventing a number.
 */
export const matchRepairOperation = (text) => {
    const normalized = normalizeRepairText(text);
    if (normalized.trim().length === 0)
        return null;
    const action = detectRepairAction(text);
    let best = null;
    let bestScore = 0;
    for (const operation of REPAIR_OPERATIONS) {
        let base = 0;
        for (const component of operation.components) {
            if (normalized.includes(component))
                base = Math.max(base, component.length);
        }
        for (const alias of operation.aliases ?? []) {
            if (normalized.includes(alias))
                base = Math.max(base, alias.length + ALIAS_BONUS);
        }
        if (base === 0)
            continue;
        let score = base;
        if (action) {
            if (operation.action === action)
                score += ACTION_BONUS;
            else if (operation.action !== 'any')
                score -= ACTION_MISMATCH_PENALTY;
        }
        if (score > bestScore) {
            bestScore = score;
            best = operation;
        }
    }
    return best && bestScore >= MIN_SCORE ? { operation: best, score: bestScore, action } : null;
};
